var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, ButtonBase, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Datepicker, localeDe, localeEn, localeIt } from "@mobiscroll/react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import '@mobiscroll/react/dist/css/mobiscroll.scss';
import { addDays, formatFullDMY, isToday, startOfDay } from "../utils/date.util";
var DAY_IN_MS = 24 * 60 * 60 * 1000;
var CALENDAR_LOCALE_BY_LANGUAGE = {
    de: localeDe,
    en: localeEn,
    it: localeIt,
};
var toPickerDate = function (value) {
    var date = value instanceof Date
        ? value
        : typeof value === "string" || typeof value === "number"
            ? new Date(value)
            : undefined;
    return date && !Number.isNaN(date.getTime())
        ? startOfDay(date)
        : undefined;
};
var stepButtonSx = {
    width: 36,
    height: 36,
    borderRadius: 1,
    color: "text.secondary",
    "&:hover": {
        backgroundColor: "action.hover",
        color: "text.primary",
    },
};
var mergeSx = function (sx) { return Array.isArray(sx) ? sx : sx ? [sx] : []; };
export function ModuleDatePicker(props) {
    var _a, _b, _c, _d, _e, _f, _g;
    var _h = useTranslation(), t = _h.t, i18n = _h.i18n;
    var pickerRef = useRef(null);
    var _j = useState(null), anchorElement = _j[0], setAnchorElement = _j[1];
    var isRange = props.variant === "range";
    var showWeekday = (_a = props.showWeekday) !== null && _a !== void 0 ? _a : true;
    var showStepButtons = (_b = props.showStepButtons) !== null && _b !== void 0 ? _b : true;
    var showTodayButton = (_c = props.showTodayButton) !== null && _c !== void 0 ? _c : true;
    var sidebarOffset = (_d = props.sidebarOffset) !== null && _d !== void 0 ? _d : 0;
    var language = (_f = (_e = i18n.language) === null || _e === void 0 ? void 0 : _e.split("-")[0]) !== null && _f !== void 0 ? _f : "en";
    var calendarLocale = useMemo(function () { var _a; return (_a = CALENDAR_LOCALE_BY_LANGUAGE[language]) !== null && _a !== void 0 ? _a : localeEn; }, [language]);
    var pickerWidth = (_g = props.width) !== null && _g !== void 0 ? _g : (isRange
        ? showWeekday
            ? 250
            : showTodayButton
                ? 210
                : 208
        : showWeekday
            ? 150
            : 140);
    var pickerValue = isRange
        ? [props.value.start, props.value.end]
        : props.value;
    var todaySelected = isRange
        ? isToday(props.value.start) && isToday(props.value.end)
        : isToday(props.value);
    var weekdayLabel = function (date) {
        return new Intl.DateTimeFormat(language, { weekday: "short" })
            .format(date)
            .replace(".", "")
            .slice(0, 2);
    };
    var renderDateLabel = function (date) {
        var dateLabel = formatFullDMY(date);
        if (!showWeekday) {
            return dateLabel;
        }
        return (_jsxs(Box, __assign({ component: "span", sx: {
                display: "inline-flex",
                alignItems: "center",
                minWidth: 0,
            } }, { children: [_jsx(Box, __assign({ component: "span", sx: {
                        width: "2ch",
                        flex: "0 0 2ch",
                        mr: 0.35,
                    } }, { children: weekdayLabel(date) })), _jsx(Box, __assign({ component: "span", sx: { minWidth: 0 } }, { children: dateLabel }))] })));
    };
    var renderTriggerLabel = function () {
        if (!isRange) {
            return renderDateLabel(props.value);
        }
        var _a = props.value, start = _a.start, end = _a.end;
        if (formatFullDMY(start) === formatFullDMY(end)) {
            return renderDateLabel(start);
        }
        return (_jsxs(Box, __assign({ component: "span", sx: {
                display: "inline-flex",
                alignItems: "center",
                minWidth: 0,
            } }, { children: [renderDateLabel(start), _jsx(Box, __assign({ component: "span", sx: { mx: 0.5, flex: "0 0 auto" } }, { children: "-" })), renderDateLabel(end)] })));
    };
    var openPicker = function () {
        var _a;
        if (!props.disabled) {
            (_a = pickerRef.current) === null || _a === void 0 ? void 0 : _a.open();
        }
    };
    var navigateByDays = function (days) {
        if (props.disabled) {
            return;
        }
        if (isRange) {
            props.onChange({
                start: startOfDay(addDays(props.value.start, days)),
                end: startOfDay(addDays(props.value.end, days)),
            });
            return;
        }
        props.onChange(startOfDay(addDays(props.value, days)));
    };
    var goToToday = function () {
        if (props.disabled) {
            return;
        }
        var today = startOfDay(new Date());
        if (isRange) {
            var currentRangeLengthDays = Math.max(0, Math.round((startOfDay(props.value.end).getTime() - startOfDay(props.value.start).getTime()) /
                DAY_IN_MS));
            props.onChange({
                start: today,
                end: addDays(today, currentRangeLengthDays),
            });
            return;
        }
        props.onChange(today);
    };
    var handlePickerChange = function (_a) {
        var _b, _c;
        var value = _a.value;
        if (isRange) {
            var rawValues = Array.isArray(value) ? value : [value, value];
            var rawStart = rawValues[0];
            var rawEnd = (_b = rawValues[1]) !== null && _b !== void 0 ? _b : rawStart;
            var start = toPickerDate(rawStart);
            var end = (_c = toPickerDate(rawEnd)) !== null && _c !== void 0 ? _c : start;
            if (!start || !end) {
                return;
            }
            props.onChange({ start: start, end: end });
            return;
        }
        var rawDate = Array.isArray(value) ? value[0] : value;
        var date = toPickerDate(rawDate);
        if (date) {
            props.onChange(date);
        }
    };
    var disabledTriggerSx = props.disabled
        ? {
            borderColor: "action.disabled",
            backgroundColor: "action.disabledBackground",
            boxShadow: "none",
            color: "text.disabled",
            cursor: "not-allowed",
            "&:hover": {
                borderColor: "action.disabled",
                backgroundColor: "action.disabledBackground",
            },
            "&:focus-within": {
                borderColor: "action.disabled",
                borderWidth: 1,
                boxShadow: "none",
            },
        }
        : {
            cursor: "default",
            "&:hover": {
                borderColor: "text.primary",
            },
            "&:focus-within": {
                borderColor: "primary.main",
                borderWidth: 2,
                boxShadow: "none",
            },
        };
    return (_jsxs(Box, __assign({ sx: __spreadArray([
            {
                height: 92,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                pl: "".concat(sidebarOffset, "px"),
            }
        ], mergeSx(props.sx), true) }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [showStepButtons && (_jsx(IconButton, __assign({ "aria-label": t("Previous day"), disabled: props.disabled, onClick: function () { return navigateByDays(-1); }, sx: stepButtonSx }, { children: _jsx(ChevronLeftIcon, { fontSize: "small" }) }))), _jsxs(Box, __assign({ ref: setAnchorElement, "aria-disabled": props.disabled ? "true" : undefined, sx: __assign({ width: pickerWidth, height: 40, display: "flex", alignItems: "center", boxSizing: "border-box", borderRadius: 1, border: "1px solid", borderColor: "rgba(0, 0, 0, 0.23)", backgroundColor: "background.paper", boxShadow: "none", overflow: "hidden" }, disabledTriggerSx) }, { children: [showTodayButton && (_jsx(Tooltip, __assign({ title: t("Set to today"), arrow: true }, { children: _jsx("span", { children: _jsx(IconButton, __assign({ "aria-label": t("Set to today"), disabled: props.disabled, size: "small", onClick: function (event) {
                                            event.stopPropagation();
                                            goToToday();
                                        }, sx: {
                                            width: 34,
                                            height: 34,
                                            mx: 0.25,
                                            borderRadius: 1,
                                            color: todaySelected ? "secondary.main" : "text.secondary",
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                                color: todaySelected ? "secondary.main" : "text.primary",
                                            },
                                        } }, { children: _jsx(CalendarMonthIcon, { sx: { fontSize: 18 } }) })) }) }))), _jsx(ButtonBase, __assign({ disabled: props.disabled, onClick: openPicker, "aria-label": isRange ? t("Select date range") : t("Select date"), sx: {
                                    flex: 1,
                                    alignSelf: "stretch",
                                    minWidth: 0,
                                    justifyContent: "flex-start",
                                    px: showTodayButton ? 0.75 : 1.25,
                                    color: props.disabled ? "text.disabled" : "text.primary",
                                    textAlign: "left",
                                    cursor: props.disabled ? "not-allowed" : "pointer",
                                } }, { children: _jsx(Typography, __assign({ noWrap: true, sx: {
                                        width: "100%",
                                        minWidth: 0,
                                        fontSize: 14,
                                        fontWeight: 500,
                                        lineHeight: "18px",
                                        mt: "2px",
                                        textAlign: "left",
                                        fontVariantNumeric: "tabular-nums",
                                    } }, { children: renderTriggerLabel() })) }))] })), showStepButtons && (_jsx(IconButton, __assign({ "aria-label": t("Next day"), disabled: props.disabled, onClick: function () { return navigateByDays(1); }, sx: stepButtonSx }, { children: _jsx(ChevronRightIcon, { fontSize: "small" }) })))] })), _jsx(Datepicker, { ref: pickerRef, disabled: props.disabled, value: pickerValue, onChange: handlePickerChange, locale: calendarLocale, theme: "material", themeVariant: "light", dateFormat: showWeekday ? "DDD DD.MM.YYYY" : "DD.MM.YYYY", controls: ["calendar"], firstDay: 1, select: isRange ? "range" : "date", showInput: false, showOnClick: false, showOnFocus: false, anchor: anchorElement || undefined, responsive: {
                    xsmall: {
                        controls: ["calendar"],
                        display: "bottom",
                        touchUi: true,
                    },
                    small: {
                        controls: ["calendar"],
                        display: "anchored",
                        touchUi: false,
                    },
                    custom: {
                        breakpoint: 800,
                        controls: ["calendar"],
                        display: "anchored",
                        touchUi: false,
                    },
                } })] })));
}
export default ModuleDatePicker;
